import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  getApprovalDashboard, 
  getPendingApprovals, 
  approveUser, 
  rejectUser, 
  bulkApprovalAction,
  getApprovedUsers,
  getRejectedUsers,
  getUserDetailsForApproval
} from "@/lib/api";
import { 
  UserCheck, UserX, Users, Building2, Stethoscope, Clock, 
  CheckCircle, XCircle, AlertCircle, RefreshCw, Eye, UserPlus,
  Calendar, Mail, Phone, MapPin, Briefcase, Shield
} from "lucide-react";

export default function AdminApprovalDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [userToReject, setUserToReject] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    if (authLoading) return; // Wait for auth to complete
    
    if (!user || !["admin", "administrator"].includes(user.role?.toLowerCase())) {
      navigate("/", { replace: true });
      return;
    }
    loadData();
  }, [user, navigate, authLoading]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashboardData, pendingData] = await Promise.all([
        getApprovalDashboard(),
        getPendingApprovals(selectedCategory === "all" ? null : selectedCategory)
      ]);
      setDashboard(dashboardData);
      setPendingUsers(pendingData.pending || []);
    } catch (error) {
      toast.error("Failed to load approval data");
    } finally {
      setLoading(false);
    }
  };

  const loadApproved = async () => {
    try {
      const data = await getApprovedUsers(selectedCategory === "all" ? null : selectedCategory);
      setApprovedUsers(data.approved || []);
    } catch (error) {
      toast.error("Failed to load approved users");
    }
  };

  const loadRejected = async () => {
    try {
      const data = await getRejectedUsers();
      setRejectedUsers(data.rejected || []);
    } catch (error) {
      toast.error("Failed to load rejected users");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "approved" && approvedUsers.length === 0) {
      loadApproved();
    } else if (tab === "rejected" && rejectedUsers.length === 0) {
      loadRejected();
    }
  };

  const handleCategoryChange = async (category) => {
    setSelectedCategory(category);
    try {
      const data = await getPendingApprovals(category === "all" ? null : category);
      setPendingUsers(data.pending || []);
    } catch (error) {
      toast.error("Failed to filter users");
    }
  };

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      toast.success("User approved successfully");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve user");
    }
  };

  const handleRejectClick = (user) => {
    setUserToReject(user);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!userToReject) return;
    try {
      await rejectUser(userToReject.id, rejectReason);
      toast.success("User rejected");
      setRejectDialogOpen(false);
      setUserToReject(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject user");
    }
  };

  const handleBulkApprove = async () => {
    if (selectedUsers.length === 0) {
      toast.warning("Select users to approve");
      return;
    }
    try {
      await bulkApprovalAction(selectedUsers, "approve");
      toast.success(`${selectedUsers.length} users approved`);
      setSelectedUsers([]);
      loadData();
    } catch (error) {
      toast.error("Failed to approve users");
    }
  };

  const handleBulkReject = async () => {
    if (selectedUsers.length === 0) {
      toast.warning("Select users to reject");
      return;
    }
    try {
      await bulkApprovalAction(selectedUsers, "reject", "Bulk rejection by admin");
      toast.success(`${selectedUsers.length} users rejected`);
      setSelectedUsers([]);
      loadData();
    } catch (error) {
      toast.error("Failed to reject users");
    }
  };

  const handleViewDetails = async (userId) => {
    try {
      const details = await getUserDetailsForApproval(userId);
      setSelectedUserDetails(details);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === pendingUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(pendingUsers.map(u => u.id));
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      facility: "bg-blue-100 text-blue-800",
      hospital: "bg-purple-100 text-purple-800",
      physician: "bg-green-100 text-green-800",
      staff: "bg-orange-100 text-orange-800",
      patient: "bg-teal-100 text-teal-800",
      caregiver: "bg-pink-100 text-pink-800",
      family: "bg-rose-100 text-rose-800",
      nurse: "bg-cyan-100 text-cyan-800",
      aide: "bg-amber-100 text-amber-800"
    };
    return (
      <Badge className={roleColors[role] || "bg-gray-100 text-gray-800"}>
        {role?.charAt(0).toUpperCase() + role?.slice(1)}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-approval-dashboard">
      <div className="container-max px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-serif font-medium text-foreground">User Approvals</h1>
            <p className="text-muted-foreground mt-1">Review and approve new registrations</p>
          </div>
          <Button onClick={loadData} variant="outline" data-testid="refresh-approvals-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card 
            className="bg-amber-50 border-amber-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setSelectedCategory('all'); setActiveTab('pending'); }}
            data-testid="stat-pending"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{dashboard?.total_pending || 0}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
              </div>
              <p className="text-xs text-amber-500 mt-2">View awaiting approval →</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-blue-50 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setSelectedCategory('facility'); setActiveTab('pending'); }}
            data-testid="stat-partners"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">
                    {(dashboard?.by_category?.facilities || 0) + (dashboard?.by_category?.hospitals || 0)}
                  </p>
                  <p className="text-xs text-blue-600">Partners</p>
                </div>
              </div>
              <p className="text-xs text-blue-500 mt-2">View partner approvals →</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-pink-50 border-pink-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setSelectedCategory('patient'); setActiveTab('pending'); }}
            data-testid="stat-patients"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-100 rounded-lg">
                  <Users className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-pink-700">{dashboard?.by_category?.patients || 0}</p>
                  <p className="text-xs text-pink-600">Patient/Caregiver</p>
                </div>
              </div>
              <p className="text-xs text-pink-500 mt-2">View patient approvals →</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-green-50 border-green-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setSelectedCategory('physician'); setActiveTab('pending'); }}
            data-testid="stat-physicians"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Stethoscope className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{dashboard?.by_category?.physicians || 0}</p>
                  <p className="text-xs text-green-600">Physicians</p>
                </div>
              </div>
              <p className="text-xs text-green-500 mt-2">View physician approvals →</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-purple-50 border-purple-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setSelectedCategory('staff'); setActiveTab('pending'); }}
            data-testid="stat-staff"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{dashboard?.by_category?.staff || 0}</p>
                  <p className="text-xs text-purple-600">Staff</p>
                </div>
              </div>
              <p className="text-xs text-purple-500 mt-2">View staff approvals →</p>
            </CardContent>
          </Card>

          <Card 
            className="bg-teal-50 border-teal-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => { setActiveTab('approved'); loadApproved(); }}
            data-testid="stat-this-week"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-teal-700">{dashboard?.approved_this_week || dashboard?.recent_registrations || 0}</p>
                  <p className="text-xs text-teal-600">This Week</p>
                </div>
              </div>
              <p className="text-xs text-teal-500 mt-2">Approved this week →</p>
            </CardContent>
          </Card>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { key: "all", label: "All", icon: Users },
            { key: "partners", label: "Partners", icon: Building2 },
            { key: "physicians", label: "Physicians", icon: Stethoscope },
            { key: "staff", label: "Staff", icon: Briefcase },
            { key: "patients", label: "Patients/Caregivers", icon: UserPlus }
          ].map(({ key, label, icon: Icon }) => (
            <Button
              key={key}
              variant={selectedCategory === key ? "default" : "outline"}
              size="sm"
              onClick={() => handleCategoryChange(key)}
              data-testid={`filter-${key}-btn`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {label}
            </Button>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6">
            <TabsTrigger value="pending" data-testid="tab-pending">
              <Clock className="w-4 h-4 mr-2" />
              Pending ({dashboard?.total_pending || 0})
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">
              <CheckCircle className="w-4 h-4 mr-2" />
              Approved
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">
              <XCircle className="w-4 h-4 mr-2" />
              Rejected
            </TabsTrigger>
          </TabsList>

          {/* Pending Tab */}
          <TabsContent value="pending">
            {pendingUsers.length > 0 && (
              <div className="flex items-center gap-4 mb-4 p-4 bg-muted rounded-lg">
                <Checkbox 
                  checked={selectedUsers.length === pendingUsers.length && pendingUsers.length > 0}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm text-muted-foreground">
                  {selectedUsers.length > 0 ? `${selectedUsers.length} selected` : "Select all"}
                </span>
                {selectedUsers.length > 0 && (
                  <>
                    <Button size="sm" onClick={handleBulkApprove} className="bg-green-600 hover:bg-green-700" data-testid="bulk-approve-btn">
                      <UserCheck className="w-4 h-4 mr-2" />
                      Approve Selected
                    </Button>
                    <Button size="sm" variant="destructive" onClick={handleBulkReject} data-testid="bulk-reject-btn">
                      <UserX className="w-4 h-4 mr-2" />
                      Reject Selected
                    </Button>
                  </>
                )}
              </div>
            )}

            {pendingUsers.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-medium mb-2">All Caught Up!</h3>
                  <p className="text-muted-foreground">No pending approvals at this time.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingUsers.map((pendingUser) => (
                  <Card key={pendingUser.id} className="hover:shadow-md transition-shadow" data-testid={`pending-user-${pendingUser.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <Checkbox 
                          checked={selectedUsers.includes(pendingUser.id)}
                          onCheckedChange={() => toggleUserSelection(pendingUser.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-lg">{pendingUser.name}</h3>
                                {getRoleBadge(pendingUser.role)}
                                {pendingUser.days_pending > 3 && (
                                  <Badge variant="destructive" className="text-xs">
                                    {pendingUser.days_pending} days waiting
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground space-y-1">
                                <p className="flex items-center gap-2">
                                  <Mail className="w-4 h-4" />
                                  {pendingUser.email}
                                </p>
                                {pendingUser.organization_name && (
                                  <p className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4" />
                                    {pendingUser.organization_name}
                                  </p>
                                )}
                                {pendingUser.phone && (
                                  <p className="flex items-center gap-2">
                                    <Phone className="w-4 h-4" />
                                    {pendingUser.phone}
                                  </p>
                                )}
                                {pendingUser.specialty && (
                                  <p className="flex items-center gap-2">
                                    <Stethoscope className="w-4 h-4" />
                                    {pendingUser.specialty}
                                  </p>
                                )}
                                <p className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4" />
                                  Registered: {formatDate(pendingUser.created_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => handleViewDetails(pendingUser.id)}
                                data-testid={`view-details-${pendingUser.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Details
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApprove(pendingUser.id)}
                                data-testid={`approve-${pendingUser.id}`}
                              >
                                <UserCheck className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleRejectClick(pendingUser)}
                                data-testid={`reject-${pendingUser.id}`}
                              >
                                <UserX className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Approved Tab */}
          <TabsContent value="approved">
            {approvedUsers.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No recently approved users to display.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {approvedUsers.map((approvedUser) => (
                  <Card key={approvedUser.id} className="bg-green-50/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{approvedUser.name}</span>
                              {getRoleBadge(approvedUser.role)}
                            </div>
                            <p className="text-sm text-muted-foreground">{approvedUser.email}</p>
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <p>Approved: {formatDate(approvedUser.approved_at)}</p>
                          {approvedUser.approved_by_name && (
                            <p>By: {approvedUser.approved_by_name}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Rejected Tab */}
          <TabsContent value="rejected">
            {rejectedUsers.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No rejected users to display.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {rejectedUsers.map((rejectedUser) => (
                  <Card key={rejectedUser.id} className="bg-red-50/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <XCircle className="w-5 h-5 text-red-600" />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{rejectedUser.name}</span>
                              {getRoleBadge(rejectedUser.role)}
                            </div>
                            <p className="text-sm text-muted-foreground">{rejectedUser.email}</p>
                            {rejectedUser.rejection_reason && (
                              <p className="text-sm text-red-600 mt-1">
                                Reason: {rejectedUser.rejection_reason}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <p>Rejected: {formatDate(rejectedUser.rejected_at)}</p>
                          {rejectedUser.rejected_by_name && (
                            <p>By: {rejectedUser.rejected_by_name}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Registration</DialogTitle>
              <DialogDescription>
                Are you sure you want to reject {userToReject?.name}? You can optionally provide a reason.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder="Reason for rejection (optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleRejectConfirm} data-testid="confirm-reject-btn">
                Reject User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* User Details Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
            </DialogHeader>
            {selectedUserDetails && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-lg font-medium text-primary">
                      {selectedUserDetails.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium text-lg">{selectedUserDetails.name}</h3>
                    {getRoleBadge(selectedUserDetails.role)}
                  </div>
                </div>

                <div className="grid gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedUserDetails.email}</span>
                  </div>
                  {selectedUserDetails.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedUserDetails.phone}</span>
                    </div>
                  )}
                  {selectedUserDetails.organization_name && (
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedUserDetails.organization_name}</span>
                    </div>
                  )}
                  {selectedUserDetails.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedUserDetails.address}</span>
                    </div>
                  )}
                  {selectedUserDetails.specialty && (
                    <div className="flex items-center gap-2">
                      <Stethoscope className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedUserDetails.specialty}</span>
                    </div>
                  )}
                  {selectedUserDetails.npi_number && (
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-muted-foreground" />
                      <span>NPI: {selectedUserDetails.npi_number}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>Registered: {formatDate(selectedUserDetails.created_at)}</span>
                  </div>
                </div>

                {(selectedUserDetails.assigned_patients !== undefined || selectedUserDetails.uploaded_documents !== undefined) && (
                  <div className="pt-3 border-t">
                    <h4 className="font-medium mb-2">Activity</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedUserDetails.assigned_patients !== undefined && (
                        <div className="bg-muted rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold">{selectedUserDetails.assigned_patients}</p>
                          <p className="text-xs text-muted-foreground">Assigned Patients</p>
                        </div>
                      )}
                      {selectedUserDetails.uploaded_documents !== undefined && (
                        <div className="bg-muted rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold">{selectedUserDetails.uploaded_documents}</p>
                          <p className="text-xs text-muted-foreground">Uploaded Documents</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
              {selectedUserDetails && !selectedUserDetails.approved && (
                <>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      handleApprove(selectedUserDetails.id);
                      setDetailDialogOpen(false);
                    }}
                  >
                    <UserCheck className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => {
                      setDetailDialogOpen(false);
                      handleRejectClick(selectedUserDetails);
                    }}
                  >
                    <UserX className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
